import { CircularProgress } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Job } from "store/jobsStore";

import { getInterforumEdiJobsInProgress } from "./interforumEdiJobUtils";
import styles from "./style.module.scss";

type InterforumJobMetadata = {
    fileName?: string;
    ediIdentifier?: string;
};

interface InterforumProcessingBannerProps {
    jobs: Job[];
    isUploading: boolean;
}

const InterforumProcessingBanner = ({ jobs, isUploading }: InterforumProcessingBannerProps) => {
    const [t] = useTranslation();

    const processingJobs = useMemo(() => getInterforumEdiJobsInProgress(jobs), [jobs]);

    const activeJob = useMemo(() => {
        if (!processingJobs.length) {
            return null;
        }

        return [...processingJobs].sort(
            (a, b) => new Date(b.modifiedAt ?? b.createdAt).getTime() - new Date(a.modifiedAt ?? a.createdAt).getTime(),
        )[0];
    }, [processingJobs]);

    if (!isUploading && !activeJob) {
        return null;
    }

    const metadata = activeJob?.metadata as InterforumJobMetadata | undefined;
    const fileName = metadata?.fileName;

    const bannerText = isUploading
        ? t("Settings.EDIInterforum.ProcessingBannerUploading")
        : fileName
          ? t("Settings.EDIInterforum.ProcessingBanner", { fileName })
          : t("Settings.EDIInterforum.ProcessingBannerGeneric");

    return (
        <div className={styles["processing-banner"]} role="status" aria-live="polite">
            <CircularProgress size={16} sx={{ color: "#004cac", flexShrink: 0 }} />
            <span>{bannerText}</span>
        </div>
    );
};

export default InterforumProcessingBanner;
